import { useEffect, useMemo, useRef, useState } from "react";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { prettifyApiError } from "@/shared/api";
import { type MailAccount, type MailAccountWrite, createMailAccount, useResource } from "./api";
import { Pillola, TASTO } from "./parti";

/** Nome del canale per OGNI sorgente che il backend puo' restituire: WhatsApp
 *  e Temu non si aggiungono da qui, ma ci sono, e prima finivano sotto
 *  «Mirakl» per il ripiego su SOURCES.mirakl. */
const NOMI_SORGENTE: Record<string, string> = {
  mirakl: "Mirakl",
  amazon: "Amazon",
  metro: "Metro",
  manomano: "ManoMano",
  whatsapp: "WhatsApp",
  temu: "Temu",
};

type Source = MailAccount["source"];

const SOURCES: Record<
  Source,
  { label: string; color: string; fields: { key: string; label: string; secret?: boolean }[] }
> = {
  mirakl: {
    label: "Mirakl",
    color: "#7B4CA0",
    fields: [
      { key: "slug", label: "Slug" },
      { key: "label", label: "Etichetta" },
      { key: "base_url", label: "Base URL" },
      { key: "api_key", label: "API key", secret: true },
    ],
  },
  amazon: {
    label: "Amazon",
    color: "#D98324",
    fields: [
      { key: "slug", label: "Slug" },
      { key: "label", label: "Etichetta" },
      { key: "lwa_app_client_id", label: "LWA app client ID" },
      { key: "lwa_app_client_secret", label: "LWA client secret", secret: true },
      { key: "region", label: "Regione" },
      { key: "refresh_token", label: "Refresh token", secret: true },
      { key: "marketplace_id", label: "Marketplace ID" },
    ],
  },
  metro: {
    label: "Metro",
    color: "#2F6FA8",
    fields: [
      { key: "slug", label: "Slug" },
      { key: "label", label: "Etichetta" },
      { key: "client_id", label: "Client ID" },
      { key: "client_secret", label: "Client secret", secret: true },
      { key: "order_management_base_url", label: "Order API URL" },
      { key: "message_center_base_url", label: "Message API URL" },
    ],
  },
  manomano: {
    label: "ManoMano",
    color: "#C84C4C",
    fields: [
      { key: "slug", label: "Slug" },
      { key: "label", label: "Etichetta" },
      { key: "api_key", label: "API key", secret: true },
      { key: "seller_contract_id", label: "Seller contract ID" },
    ],
  },
};

const DEFAULT_VALUES: Record<string, string> = {
  region: "IT",
  marketplace_id: "APJ6JRA9NG5V4",
};

/** ``puoAggiungere``: il POST e' riservato a `god` (sono credenziali, audit
 *  02/10): agli altri il tasto risponderebbe 403. */
export function MailAccountsCard({ puoAggiungere = true }: { puoAggiungere?: boolean }) {
  const accounts = useResource<MailAccount[]>("/api/mail/accounts", []);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="px-5 py-4 max-md:px-4">
      <div className="mb-3 flex items-start justify-between gap-3 max-md:flex-col">
        <div>
          <h3 className="text-[15px] font-bold text-ink max-md:text-[17px]">Account dei canali</h3>
          <p className="mt-0.5 text-[12.5px] text-ink-2 max-md:text-[14px] max-md:text-ink">
            Gli account da cui OM legge e scrive messaggi. Le chiavi non si vedono mai.
          </p>
        </div>
        {puoAggiungere && (
          <button type="button" onClick={() => setIsOpen(true)} className={TASTO}>
            Aggiungi un account
          </button>
        )}
      </div>

      <MailAccountsList
        accounts={accounts.value}
        loading={accounts.isLoading}
        error={accounts.error}
      />

      {isOpen && (
        <MailAccountModal
          onClose={() => setIsOpen(false)}
          onSaved={() => {
            setIsOpen(false);
            void accounts.refresh();
          }}
        />
      )}
    </div>
  );
}

function MailAccountsList({
  accounts,
  loading,
  error,
}: {
  accounts: MailAccount[];
  loading: boolean;
  error: string | null;
}) {
  if (loading && accounts.length === 0) {
    return <p className="py-2 text-[13px] text-ink-2">Carico gli account…</p>;
  }
  if (error) {
    return <ErrorBanner message={error} />;
  }
  if (accounts.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-line-2 px-3 py-3 text-[13px] text-ink">
        Nessun account configurato.
      </p>
    );
  }
  return (
    <ul className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
      {accounts.map((account) => (
        <li
          key={account.id}
          className="flex items-center justify-between gap-3 border-t border-line py-2.5 first:border-t-0 sm:[&:nth-child(2)]:border-t-0"
        >
          <div className="min-w-0">
            <div className="truncate text-[13.5px] font-bold text-ink max-md:text-[15px]">
              {account.label}
            </div>
            <div className="text-[12px] text-ink-2 max-md:text-[13.5px] max-md:text-ink">
              {NOMI_SORGENTE[account.source] ?? account.source} · {account.slug}
            </div>
          </div>
          {/* Stesse parole del badge gemello in UsersCard: la pagina è tutta in italiano. */}
          <Pillola tono={account.enabled ? "ok" : "spento"}>
            {account.enabled ? "Attivo" : "Disattivato"}
          </Pillola>
        </li>
      ))}
    </ul>
  );
}

function MailAccountModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [source, setSource] = useState<Source>("mirakl");
  const [values, setValues] = useState<Record<string, string>>(DEFAULT_VALUES);
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const fields = SOURCES[source].fields;
  const requiredKeys = useMemo(() => fields.map((field) => field.key), [fields]);

  // Audit 2026-05-29 [LOW]: a11y del dialog, stesso pattern di CredentialsDialog
  // (Fornitori). ESC chiude (listener window-level con cleanup), focus iniziale
  // sul primo campo all'apertura. Il focus-trap completo richiederebbe una
  // libreria dedicata: qui ci limitiamo a portare il focus dentro al dialog.
  const firstFieldRef = useRef<HTMLSelectElement | null>(null);
  useEffect(() => {
    function handleKey(event: KeyboardEvent) {
      // Non chiudere mentre il salvataggio è in volo: lo smontaggio a metà
      // farebbe cadere setError su un componente smontato (esito invisibile).
      if (event.key === "Escape" && !isSaving) onClose();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isSaving, onClose]);
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  function setValue(key: string, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const missing = requiredKeys.filter((key) => !String(values[key] ?? "").trim());
    if (missing.length > 0) {
      setError("Compila tutti i campi obbligatori.");
      return;
    }
    // Audit 2026-05-11 M11: validazione esplicita di ``seller_contract_id``
    // come intero positivo. Prima ``Number("")`` produceva ``0`` e
    // ``Number("abc")`` produceva ``NaN`` (serializzato come ``null``);
    // entrambi salvavano dati invalidi nel DB senza che l'utente lo sapesse.
    const sellerContractId = values.seller_contract_id?.trim();
    if (sellerContractId !== undefined && sellerContractId !== "") {
      const parsed = Number(sellerContractId);
      if (!Number.isInteger(parsed) || parsed <= 0) {
        setError("Seller contract ID deve essere un numero intero positivo.");
        return;
      }
    }
    setIsSaving(true);
    try {
      await createMailAccount(toPayload(source, values, enabled));
      setError(null);
      onSaved();
    } catch (err) {
      // Audit 2026-05-29 [LOW]: errore leggibile in italiano, niente err.message grezzo.
      setError(prettifyApiError(err));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    // Audit 2026-05-29 [LOW]: overlay con semantica dialog + chiusura su click
    // fuori (onClick sull'overlay). Il form ferma la propagazione cosi' il
    // click interno non chiude il modale.
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mail-account-modal-title"
      onClick={() => {
        if (!isSaving) onClose();
      }}
      className="fixed inset-0 z-30 flex items-center justify-center bg-ink/30 px-4"
    >
      <form
        onSubmit={(event) => void submit(event)}
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-[560px] rounded-lg border border-line bg-card p-5 shadow-popover"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 id="mail-account-modal-title" className="font-display text-[20px] font-bold">
              Aggiungi marketplace
            </h3>
            <p className="text-[12.5px] text-ink-2">I secret vengono salvati nel keyring.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-lg border border-line px-2 py-1 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft"
          >
            Chiudi
          </button>
        </div>

        <label className="mb-3 block text-[12px] font-medium text-ink-2">
          Marketplace
          <select
            ref={firstFieldRef}
            value={source}
            onChange={(event) => setSource(event.target.value as Source)}
            className="mt-1 w-full rounded-lg border border-line bg-paper px-3 py-2 text-[13px] focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-soft"
          >
            {Object.entries(SOURCES).map(([key, item]) => (
              <option key={key} value={key}>
                {item.label}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          {fields.map((field) => (
            <label key={field.key} className="block text-[12px] font-medium text-ink-2">
              {field.label}
              <input
                type={field.secret ? "password" : "text"}
                value={values[field.key] ?? ""}
                onChange={(event) => setValue(field.key, event.target.value)}
                className="mt-1 w-full rounded-lg border border-line bg-paper px-3 py-2 text-[13px] focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-soft"
              />
            </label>
          ))}
        </div>

        <label className="mt-3 flex items-center gap-2 text-[12px] text-ink-2">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          Abilitato
        </label>

        <ErrorBanner message={error} className="mt-3" />

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-lg border border-line px-3 py-2 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="rounded-lg border border-accent bg-accent-2 px-3 py-2 text-white disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft"
          >
            {isSaving ? "Salvataggio..." : "Salva"}
          </button>
        </div>
      </form>
    </div>
  );
}

function toPayload(
  source: Source,
  values: Record<string, string>,
  enabled: boolean,
): MailAccountWrite {
  const config: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(values)) {
    if (["slug", "label", "base_url"].includes(key)) continue;
    config[key] = key === "seller_contract_id" ? Number(value) : value;
  }
  return {
    source,
    slug: (values.slug ?? "").trim(),
    label: (values.label ?? "").trim(),
    base_url: (values.base_url ?? values.order_management_base_url ?? "").trim(),
    enabled,
    config,
  };
}
